import React, { FC, useMemo } from 'react';
import styles from './index.module.scss';
import { SwitchStatusType, SwitchStatusTypeFormat } from '@/api/circuit';
import { Tag, Tooltip } from '@maxtropy/components';
import { StationType, SwitchStatusColor } from '../../const';
import { Progress } from 'antd';
import { findItemById } from '../../utils';
import classNames from 'classnames';
import { V2PowerRoomConfigPowerSystemConfigurationTreePostResponse } from '@maxtropy/device-customer-apis-v2';
import { isNil } from 'lodash-es';
import { keepPercentOrLine, keepPercentTwoNull } from '@/utils/utils';
import icon_circuit from '../imgs/icon-circuit.png';
import { calcPadding, calcProcessColor, getStationCardMinHeight, noChildWrapperStyle, style } from './utils';

export interface IEachStationVertical {
  item: any;
  data?: V2PowerRoomConfigPowerSystemConfigurationTreePostResponse;
  getItemInfo?: (visible: boolean, recordData: any) => void;
  getSelectIndex?: (val?: string) => void;
  activeIndex?: string;
}

const EachStationVertical: FC<IEachStationVertical> = props => {
  const { item, data, getItemInfo, getSelectIndex, activeIndex } = props;
  const currentCircuitRoots = useMemo(
    () => (item?.powerSystemBusPaiList ?? []).flatMap((bus: any) => bus?.powerSystemConfigurationTree ?? []),
    [item]
  );

  const handleOnClickNode = (item: any) => {
    if (item?.type === StationType.DistributionStation) {
      const res = findItemById(data, item.id);
      if (activeIndex === item.id) {
        getItemInfo?.(false, undefined);
        getSelectIndex?.(undefined);
      } else {
        getItemInfo?.(true, res);
        getSelectIndex?.(item.id);
      }
    }
  };

  const renderStationCard = (child: any, width: number | string, siblings: any[], level: number) => {
    const visibleMonitorItems = (child?.monitorItemList ?? []).filter(
      (monitorItem: any) => !isNil(monitorItem.loadRate)
    );
    const useNaturalContentHeight = level >= 3;

    return (
      <div
        className={classNames(styles.level_next_noChildren, useNaturalContentHeight && styles.station_natural_card)}
        data-station-level={level}
        data-station-name={child.name}
        style={{
          ...style(false),
          ...(useNaturalContentHeight ? { flex: '0 0 auto', height: 'auto', overflow: 'visible' } : {}),
        }}
      >
        <div
          className={styles.level_next_noChildren_wrapper}
          style={{
            width,
            flexShrink: 0,
            ...(useNaturalContentHeight ? { height: 'auto', alignItems: 'flex-start' } : {}),
          }}
        >
          <div
            className={classNames(styles.noChildren_each_level, activeIndex === child.id ? styles.active : undefined)}
            style={{
              ...noChildWrapperStyle(level, width, siblings, child),
              minHeight: getStationCardMinHeight(child),
              ...(useNaturalContentHeight ? { height: 'auto', flex: '0 0 auto', overflow: 'visible' } : {}),
            }}
            onClick={() => handleOnClickNode(child)}
          >
            <div className={styles.name} style={{ paddingBottom: calcPadding(child, false) }}>
              <Tooltip title={child.name}>{child.name}</Tooltip>
            </div>
            {visibleMonitorItems.length > 0 && (
              <div className={styles.loadRate_wrapper} style={{ padding: calcPadding(child, true) }}>
                {visibleMonitorItems.map((c: any, idx2: number) => (
                  <div
                    className={styles.loadRate}
                    key={c.id + '-' + idx2}
                    style={{
                      flex: useNaturalContentHeight ? '0 0 auto' : siblings.length > 1 ? 1 : '0 0 calc(50% - 4px)',
                    }}
                  >
                    <div className={styles.label_wrapper}>
                      <div className={styles.label}>
                        <Tooltip placement="top" title={`监测回路：${c.circuitName}`}>
                          <img src={icon_circuit} className={styles.icon} alt="" />
                        </Tooltip>
                        负载率：
                      </div>
                      <div className={styles.value}>{keepPercentOrLine(c.loadRate)}%</div>
                    </div>
                    <Progress
                      className={styles.process}
                      percent={keepPercentTwoNull(c.loadRate)!}
                      showInfo={false}
                      strokeColor={calcProcessColor(keepPercentTwoNull(c.loadRate))}
                      size="small"
                      trailColor="rgba(0,0,0,0.5)"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const stationCardMinWidth = 160;
  const stationCardHeightAllowance = 16;
  const stationColumnGap = 8;
  const stationBatchGap = 16;
  const maxStationColumns = 3;
  const maxStationRows = 8;
  const maxStationsPerBatch = maxStationColumns * maxStationRows;

  const distributeEvenly = (items: any[], columnCount: number) => {
    let offset = 0;
    return Array.from({ length: columnCount }, (_, columnIndex) => {
      const columnSize = Math.floor(items.length / columnCount) + (columnIndex < items.length % columnCount ? 1 : 0);
      const column = items.slice(offset, offset + columnSize);
      offset += columnSize;
      return column;
    });
  };

  const splitIntoColumnBatches = (nodes: any[]) => {
    const branchNodes = nodes.filter(node => node?.children?.length);
    const leafNodes = nodes.filter(node => !node?.children?.length);
    const batches: any[][][] = [];

    while (branchNodes.length) {
      const branches = branchNodes.splice(0, maxStationColumns);
      const columns = branches.map(branch => [branch]);
      const availableLeafColumns = maxStationColumns - branches.length;
      const currentLeafCapacity = availableLeafColumns * maxStationRows;
      const currentLeafCount = Math.min(leafNodes.length, currentLeafCapacity);
      const leafColumnCount = Math.min(availableLeafColumns, Math.ceil(currentLeafCount / maxStationRows));
      const leafBatch = leafNodes.splice(0, leafColumnCount * maxStationRows);
      const leafColumns = leafColumnCount > 0 ? distributeEvenly(leafBatch, leafColumnCount) : [];
      batches.push([...columns, ...leafColumns]);
    }

    while (leafNodes.length) {
      const batch = leafNodes.splice(0, maxStationsPerBatch);
      const columnCount = Math.min(maxStationColumns, Math.ceil(batch.length / maxStationRows));
      batches.push(distributeEvenly(batch, columnCount));
    }

    return batches;
  };

  const getStationCardWidth = (node: any): number => {
    const batches = splitIntoColumnBatches(node?.children ?? []);
    if (!batches.length) return stationCardMinWidth;

    const childrenWidth =
      batches.reduce((batchTotal, columns) => {
        const batchWidth =
          columns.reduce((columnTotal, column) => {
            const columnWidth = Math.max(...column.map(child => getStationCardWidth(child)), stationCardMinWidth);
            return columnTotal + columnWidth;
          }, 0) +
          Math.max(0, columns.length - 1) * stationColumnGap;
        return batchTotal + batchWidth;
      }, 0) +
      Math.max(0, batches.length - 1) * stationBatchGap;

    return Math.max(stationCardMinWidth, childrenWidth);
  };

  /**
   * Level-three cards keep their shared horizontal band. Deeper descendants
   * are rendered immediately below their parent branch instead of waiting for
   * a global level row. Large sibling lists still use at most three columns
   * and eight rows per column.
   */
  const renderLevelCards = (nodes: any[], level: number, parentId: number | string) => (
    <div className={styles.station_level_batches} data-parent-id={parentId}>
      {splitIntoColumnBatches(nodes).map((columns: any[][], batchIndex: number) => (
        <div
          className={classNames(styles.station_level_cards, level >= 3 && styles.station_natural_level_cards)}
          data-level={level}
          key={`${parentId}-${level}-batch-${batchIndex}`}
        >
          {columns.map((column: any[], columnIndex: number) => {
            const columnCardWidth = Math.max(...column.map(node => getStationCardWidth(node)), stationCardMinWidth);
            return (
              <div className={styles.station_level_column} key={`${parentId}-${level}-${batchIndex}-${columnIndex}`}>
                {column.map(node => (
                  <div className={styles.station_inline_branch} key={`${node.id}-${level}`}>
                    {renderStationCard(node, `${columnCardWidth}px`, column, level)}
                    {(node?.children ?? []).length > 0 && renderLevelCards(node.children, level + 1, node.id)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  const getMaxLevel = (nodes: any[], currentLevel = 2): number =>
    nodes.reduce(
      (max, node) =>
        Math.max(max, node?.children?.length ? getMaxLevel(node.children, currentLevel + 1) : currentLevel),
      currentLevel
    );

  const getLevelParents = (nodes: any[], targetLevel: number, currentLevel = 2): any[] => {
    if (currentLevel + 1 === targetLevel) return nodes;
    return getLevelParents(
      nodes.flatMap(node => node?.children ?? []),
      targetLevel,
      currentLevel + 1
    );
  };

  const renderChildren = (children: any[], isShowBorder: boolean) => {
    const maxLevel = getMaxLevel(children);
    const layoutRoots = currentCircuitRoots.length ? currentCircuitRoots : children;
    const getColumnContentHeight = (column: any[]) =>
      column.reduce(
        (total, node) => total + getStationCardMinHeight(node) + stationCardHeightAllowance,
        Math.max(0, column.length - 1) * stationColumnGap
      );
    const getLevelRowMinHeight = (level: number) => {
      if (level === 2) {
        return layoutRoots.reduce(
          (levelMax, node) => Math.max(levelMax, getStationCardMinHeight(node) + stationCardHeightAllowance),
          40 + stationCardHeightAllowance
        );
      }

      return getLevelParents(layoutRoots, level).reduce(
        (levelMax, parent) =>
          splitIntoColumnBatches(parent?.children ?? []).reduce(
            (batchMax, batch) =>
              batch.reduce((columnMax, column) => Math.max(columnMax, getColumnContentHeight(column)), batchMax),
            levelMax
          ),
        40 + stationCardHeightAllowance
      );
    };
    return (
      <div className={styles.station_hierarchy} style={style(isShowBorder)}>
        <div className={styles.station_level_row} data-level="2" style={{ minHeight: getLevelRowMinHeight(2) }}>
          {(children ?? []).map((node: any, index: number, roots: any[]) => (
            <div
              className={styles.station_level_group}
              style={{ width: getStationCardWidth(node) }}
              key={`station-root-${node.id}-${index}`}
            >
              {renderStationCard(node, `${getStationCardWidth(node)}px`, roots, 2)}
            </div>
          ))}
        </div>
        {(maxLevel >= 3 ? [3] : []).map(level => (
          <div
            className={styles.station_level_row}
            data-level={level}
            style={{ minHeight: getLevelRowMinHeight(level) }}
            key={`station-level-row-${level}`}
          >
            {(children ?? []).map((root: any) => (
              <div
                className={styles.station_level_group}
                style={{ width: getStationCardWidth(root) }}
                key={`station-level-group-${root.id}-${level}`}
              >
                {renderLevelCards(root.children ?? [], level, root.id)}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.each_content}>
      <div className={styles.level1}>
        <div className={styles.left_line} />
        <div className={styles.level1_name}>{item.circuitName}</div>
        <div className={styles.level1_switch}>
          <Tag border="solid" color={SwitchStatusColor[item.circuitSwitchStatus as SwitchStatusType]}>
            {SwitchStatusTypeFormat[item.circuitSwitchStatus as SwitchStatusType]}
          </Tag>
        </div>
        <div className={styles.dashLine} />
        <div className={styles.fuzai}>
          <div className={styles.name}>负载率</div>
          <div className={styles.progress}>
            {!isNil(item?.loadRate) ? (
              <Tooltip title={`${keepPercentTwoNull(item.loadRate)}%`}>
                <Progress
                  type="circle"
                  size={60}
                  percent={!isNil(item.loadRate) ? keepPercentTwoNull(item.loadRate)! : undefined}
                  strokeColor={calcProcessColor(keepPercentTwoNull(item.loadRate))}
                  trailColor="rgba(0,0,0,0.5)"
                  format={() => `${keepPercentTwoNull(item.loadRate)}%`}
                />
              </Tooltip>
            ) : (
              <span style={{ color: '#999' }}>--</span>
            )}
          </div>
        </div>
        <div className={styles.right_line} />
      </div>

      <div className={styles.level_next_2}>
        {item.powerSystemBusPaiList.map((c: any, index: number) => {
          const hasNoChildren = !c?.powerSystemConfigurationTree?.length;
          return (
            <div
              key={c.id + '-' + index}
              className={classNames(styles.level_next_2_wrapper, hasNoChildren && styles.no_children_level2)}
            >
              <div className={styles.level_next_2_name}>
                <div className={styles.each_level2}>{c.busPaiName}</div>
              </div>
              <div className={styles.level_next}>
                {c?.powerSystemConfigurationTree?.length > 0 && renderChildren(c.powerSystemConfigurationTree, true)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EachStationVertical;
